/**
 * Local crash-recovery store for in-app recordings.
 *
 * Industry pattern (local-first / store-and-forward):
 * - Persist MediaRecorder chunks to IndexedDB as they arrive (survive page reload).
 * - Keep at most ONE unfinished session (recording | ready_to_upload).
 * - Delete the session as soon as Storage upload succeeds (or user discards).
 * - Never accumulate uploaded leftovers / orphan chunk piles.
 *
 * IDB is NOT a long-term archive. Server/transcription list is the source of truth
 * after a successful upload.
 */

export interface RecordingChunk {
  id: string;
  recordingId: string;
  chunkIndex: number;
  /** ArrayBuffer for reliable restore in Safari; legacy chunks may have .data (Blob) */
  buffer?: ArrayBuffer;
  data?: Blob;
  timestamp: number;
}

/** Only unfinished local work belongs in IDB */
export type RecordingPhase = "recording" | "ready_to_upload";

export interface RecordingState {
  id: string;
  isActive: boolean;
  startTime: number;
  chunks: number;
  lastUpdated: number;
  mimeType?: string;
  phase?: RecordingPhase | "uploaded_backup"; // uploaded_backup = legacy, always purged
}

const chunkKey = (recordingId: string, chunkIndex: number) => `${recordingId}_${chunkIndex}`;

const chunkKeyRange = (recordingId: string) =>
  IDBKeyRange.bound(`${recordingId}_`, `${recordingId}_\uffff`);

/** ~100 min @ 3s timeslice + small headroom. Above this → DB is considered corrupt/bloated. */
export const MAX_CHUNKS_PER_SESSION = 2_200;
/** Absolute ceiling for entire chunk store before we wipe the DB. */
const MAX_TOTAL_CHUNKS = 2_500;

export default class RecordingStateDB {
  private static dbName = "AudioRecordingState";
  private static db: IDBDatabase | null = null;
  private static readonly OPEN_TIMEOUT_MS = 8_000;

  /**
   * Open current DB version (no bump).
   * Never add secondary indexes on recordingChunks — indexing huge existing
   * stores freezes the page (indexedDB.open stays pending forever).
   */
  private static openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName);

      request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
      request.onblocked = () => {
        console.warn("[RecordingStateDB] open blocked — another connection holds the DB");
      };
      request.onsuccess = () => {
        this.db = request.result;
        this.db.onversionchange = () => {
          try {
            this.db?.close();
          } catch {
            /* ignore */
          }
          this.db = null;
        };
        resolve(this.db);
      };

      request.onupgradeneeded = () => {
        const db = (request as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("recordingChunks")) {
          db.createObjectStore("recordingChunks", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("recordingState")) {
          db.createObjectStore("recordingState", { keyPath: "id" });
        }
      };
    });
  }

  private static withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      promise.then(
        (v) => {
          clearTimeout(timer);
          resolve(v);
        },
        (e) => {
          clearTimeout(timer);
          reject(e);
        }
      );
    });
  }

  static async deleteDatabase(): Promise<void> {
    if (this.db) {
      try {
        this.db.close();
      } catch {
        /* ignore */
      }
      this.db = null;
    }

    await new Promise<void>((resolve, reject) => {
      const req = indexedDB.deleteDatabase(this.dbName);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error ?? new Error("IndexedDB delete failed"));
      req.onblocked = () => {
        console.warn("[RecordingStateDB] deleteDatabase blocked — retry after reload");
        setTimeout(() => resolve(), 2_000);
      };
    });
  }

  /** Wipe both stores in-place (faster than per-key delete when bloated). */
  static async clearAllStores(): Promise<void> {
    if (!this.db) throw new Error("Database not initialized");

    await new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction(["recordingChunks", "recordingState"], "readwrite");
      tx.objectStore("recordingChunks").clear();
      tx.objectStore("recordingState").clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  static async init(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    try {
      return await this.withTimeout(this.openDatabase(), this.OPEN_TIMEOUT_MS, "IndexedDB open");
    } catch (err) {
      console.warn("[RecordingStateDB] open failed/timed out — resetting local DB", err);
      try {
        await this.deleteDatabase();
      } catch (deleteErr) {
        console.error("[RecordingStateDB] deleteDatabase after timeout failed", deleteErr);
      }
      return await this.withTimeout(this.openDatabase(), this.OPEN_TIMEOUT_MS, "IndexedDB reopen");
    }
  }

  static async countAllChunks(): Promise<number> {
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(["recordingChunks"], "readonly");
      const req = tx.objectStore("recordingChunks").count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Crash-recovery health check. If the DB is bloated (legacy orphans), wipe it.
   * Call once after init — must stay fast (count only, or nuke).
   */
  static async ensureHealthyForCrashRecovery(): Promise<{
    action: "ok" | "cleared" | "nuked";
    totalChunks: number;
  }> {
    await this.init();

    let totalChunks = 0;
    try {
      totalChunks = await this.withTimeout(this.countAllChunks(), 5_000, "countAllChunks");
    } catch (err) {
      console.warn("[RecordingStateDB] countAllChunks failed — nuking DB", err);
      await this.deleteDatabase();
      await this.init();
      return { action: "nuked", totalChunks: -1 };
    }

    if (totalChunks > MAX_TOTAL_CHUNKS) {
      console.warn("[RecordingStateDB] bloated chunk store — clearing", { totalChunks });
      try {
        await this.withTimeout(this.clearAllStores(), 8_000, "clearAllStores");
        return { action: "cleared", totalChunks };
      } catch {
        await this.deleteDatabase();
        await this.init();
        return { action: "nuked", totalChunks };
      }
    }

    return { action: "ok", totalChunks };
  }

  /** Persist one MediaRecorder chunk (crash recovery). */
  static async saveRecordingChunk(
    recordingId: string,
    chunkIndex: number,
    chunkData: Blob
  ): Promise<void> {
    if (!this.db) throw new Error("Database not initialized");
    if (chunkIndex >= MAX_CHUNKS_PER_SESSION) {
      throw new Error(`Chunk index ${chunkIndex} exceeds max session size`);
    }

    const buffer = await chunkData.arrayBuffer();
    const transaction = this.db.transaction(["recordingChunks"], "readwrite");
    const store = transaction.objectStore("recordingChunks");

    return new Promise((resolve, reject) => {
      const request = store.put({
        id: chunkKey(recordingId, chunkIndex),
        recordingId,
        chunkIndex,
        buffer,
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  static async saveRecordingState(
    recordingId: string,
    state: Partial<RecordingState>
  ): Promise<void> {
    if (!this.db) throw new Error("Database not initialized");

    const transaction = this.db.transaction(["recordingState"], "readwrite");
    const store = transaction.objectStore("recordingState");

    return new Promise((resolve, reject) => {
      const request = store.put({
        id: recordingId,
        ...state,
        lastUpdated: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  static async getRecordingState(recordingId: string): Promise<RecordingState | undefined> {
    if (!this.db) throw new Error("Database not initialized");

    const transaction = this.db.transaction(["recordingState"], "readonly");
    const store = transaction.objectStore("recordingState");

    return new Promise((resolve, reject) => {
      const request = store.get(recordingId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /** Load chunks for one recording via key prefix — never full-store scan. */
  static async getRecordingChunks(recordingId: string): Promise<RecordingChunk[]> {
    if (!this.db) throw new Error("Database not initialized");

    const transaction = this.db.transaction(["recordingChunks"], "readonly");
    const store = transaction.objectStore("recordingChunks");
    const range = chunkKeyRange(recordingId);

    return new Promise((resolve, reject) => {
      const chunks: RecordingChunk[] = [];
      const request = store.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          chunks.push(cursor.value);
          cursor.continue();
        } else {
          chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);
          resolve(chunks);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  static async countRecordingChunks(recordingId: string): Promise<number> {
    if (!this.db) throw new Error("Database not initialized");

    const transaction = this.db.transaction(["recordingChunks"], "readonly");
    const store = transaction.objectStore("recordingChunks");

    return new Promise((resolve, reject) => {
      const request = store.count(chunkKeyRange(recordingId));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  static async getAllRecordingStates(): Promise<RecordingState[]> {
    if (!this.db) throw new Error("Database not initialized");

    const transaction = this.db.transaction(["recordingState"], "readonly");
    const store = transaction.objectStore("recordingState");

    return new Promise((resolve, reject) => {
      const recordings: RecordingState[] = [];
      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          recordings.push(cursor.value);
          cursor.continue();
        } else {
          resolve(recordings);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Sessions that should restore after page reload:
   * actively recording or stopped-but-not-uploaded. Never "uploaded" leftovers.
   */
  static async getRestorableRecordings(): Promise<RecordingState[]> {
    const all = await this.getAllRecordingStates();
    return all.filter((value) => {
      if (value.phase === "uploaded_backup") return false;
      if (value.phase === "recording" || value.phase === "ready_to_upload") return true;
      if (value.isActive) return true;
      // Legacy: stopped with chunks, never uploaded
      return !value.isActive && (value.chunks ?? 0) > 0;
    });
  }

  static async getAllActiveRecordings(): Promise<RecordingState[]> {
    return this.getRestorableRecordings();
  }

  /**
   * Delete one session. If it looks bloated, clear/nuke instead of cursor-deleting
   * hundreds of thousands of keys (that freezes the page).
   */
  static async deleteRecordingById(recordingId: string): Promise<void> {
    if (!this.db) throw new Error("Database not initialized");

    let chunkCount = 0;
    let totalChunks = 0;
    try {
      chunkCount = await this.countRecordingChunks(recordingId);
      totalChunks = await this.countAllChunks();
    } catch {
      chunkCount = MAX_CHUNKS_PER_SESSION + 1;
      totalChunks = MAX_TOTAL_CHUNKS + 1;
    }

    if (chunkCount > MAX_CHUNKS_PER_SESSION || totalChunks > MAX_TOTAL_CHUNKS) {
      console.warn("[RecordingStateDB] delete via clear/nuke (bloated)", {
        recordingId,
        chunkCount,
        totalChunks,
      });
      try {
        await this.clearAllStores();
      } catch {
        await this.deleteDatabase();
        await this.init();
      }
      return;
    }

    // Remove state first so restore cannot pick it up mid-delete
    await new Promise<void>((resolve, reject) => {
      const stateTransaction = this.db!.transaction(["recordingState"], "readwrite");
      const stateRequest = stateTransaction.objectStore("recordingState").delete(recordingId);
      stateRequest.onsuccess = () => resolve();
      stateRequest.onerror = () => reject(stateRequest.error);
    });

    let deletedChunks = 0;
    await new Promise<void>((resolve, reject) => {
      const chunkTransaction = this.db!.transaction(["recordingChunks"], "readwrite");
      const chunkStore = chunkTransaction.objectStore("recordingChunks");
      const request = chunkStore.openCursor(chunkKeyRange(recordingId));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
        if (cursor) {
          cursor.delete();
          deletedChunks += 1;
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
      chunkTransaction.onerror = () => reject(chunkTransaction.error);
    });

    console.log("[RecordingStateDB] session cleared", { recordingId, deletedChunks });
  }

  /** Alias: crash-recovery session is done (upload ok / discard). */
  static async clearSession(recordingId: string): Promise<void> {
    await this.deleteRecordingById(recordingId);
  }

  /**
   * Single-session invariant: keep at most `keepId`, drop everything else.
   * Call when starting a new recording after confirming no restorable work remains.
   */
  static async beginExclusiveSession(keepId: string): Promise<void> {
    if (!this.db) throw new Error("Database not initialized");

    const all = await this.getAllRecordingStates();
    const others = all.filter((r) => r.id !== keepId);

    if (others.length === 0) {
      // Still drop orphan chunks from previous ids if store is dirty
      try {
        const total = await this.countAllChunks();
        if (total > MAX_TOTAL_CHUNKS) {
          await this.clearAllStores();
        }
      } catch {
        /* ignore */
      }
      return;
    }

    // Fast path: if anything looks large, clear whole store then caller writes new state
    try {
      const total = await this.countAllChunks();
      if (total > MAX_CHUNKS_PER_SESSION || others.length > 0) {
        await this.clearAllStores();
        return;
      }
    } catch {
      await this.clearAllStores();
      return;
    }

    for (const rec of others) {
      await this.deleteRecordingById(rec.id);
    }
  }

  /** @deprecated use beginExclusiveSession / clearSession */
  static async deactivateAllRecordings(): Promise<void> {
    const all = await this.getAllRecordingStates();
    for (const rec of all) {
      if (rec.phase === "recording" || rec.isActive) {
        await this.saveRecordingState(rec.id, {
          ...rec,
          isActive: false,
          phase: "ready_to_upload",
        });
      }
    }
  }

  /**
   * Enforce crash-recovery policy on startup:
   * - purge legacy uploaded_backup rows
   * - keep only the newest restorable session
   * - wipe if bloated
   */
  static async enforceCrashRecoveryPolicy(): Promise<{
    keptId: string | null;
    cleared: boolean;
  }> {
    const health = await this.ensureHealthyForCrashRecovery();
    if (health.action !== "ok") {
      return { keptId: null, cleared: true };
    }

    const all = await this.getAllRecordingStates();
    const restorable = await this.getRestorableRecordings();

    // Drop legacy uploaded backups / junk states immediately
    for (const rec of all) {
      const isRestorable = restorable.some((r) => r.id === rec.id);
      if (!isRestorable) {
        try {
          await this.deleteRecordingById(rec.id);
        } catch (err) {
          console.warn("[RecordingStateDB] failed to drop non-restorable", rec.id, err);
        }
      }
    }

    if (restorable.length <= 1) {
      return { keptId: restorable[0]?.id ?? null, cleared: false };
    }

    // Multiple unfinished sessions — keep newest only
    const newest = restorable.reduce((a, b) =>
      (a.lastUpdated || 0) >= (b.lastUpdated || 0) ? a : b
    );
    for (const rec of restorable) {
      if (rec.id === newest.id) continue;
      try {
        await this.deleteRecordingById(rec.id);
      } catch (err) {
        console.warn("[RecordingStateDB] failed to drop older session", rec.id, err);
      }
    }
    return { keptId: newest.id, cleared: false };
  }
}
